import React, { useEffect, useState } from "react";
import CustomAutoComplete from "../contentFeedback/CustomAutoComplete";
import { useAppDispatch, useAppSelecter } from "../../hooks/storeHooks";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NoteOutlinedIcon from "@mui/icons-material/NoteOutlined";
import { RootState } from "../../store/store";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import apiRequest from "../../request/apiRequest/apiRequest";
import { allApis } from "../../request/config";
import { showSnack } from "../../store/slices/constants";
import { StyledTooltip } from "./SubtopicCard";
import { ArrowBack, InfoOutlined } from "@mui/icons-material";
import { getServerEnvironment } from "../../store/slices/constants/staticContents";
import {
  ColorlibConnector,
  CssStepperHorizontal,
} from "../contentFeedback/EditScreen";
import CustomContainedButton from "../../Components/CustomContainedButton";
import CustomIconButton from "../../Components/CustomIconButton";
import CustomSearch from "../../Components/CustomSearch";
import { ObjectValues } from "../login";
import { singleLineEllipsis } from "../../Components/AllCards/layouts/CardHeader";

interface Step {
  label: string;
  description: string;
  completed: boolean;
  content: () => React.ReactNode;
}

function UpdateSubtopicTopicDialog({
  filters,
  onSubmit,
  cluster,
  changeSubTopic,
  setChangeSubTopic,
}: any) {
  const dispatch = useAppDispatch();
  const { masters } = useAppSelecter(
    (state: RootState) => state.contentFeedback
  );
  const [tableData, setTableData] = useState<ObjectValues>({
    actual: [],
    copy: [],
  });
  const dFilter = {
    exam: "",
    exam_id: "",
    goal: "",
    goal_id: "",
    section: "",
    section_id: "",
    topic: "",
    topic_id: "",
    subtopic: "",
    subtopic_id: "",
    cluster: "",
    cluster_id: "",
  };

  const localFiltersValues = localStorage.getItem(
    btoa(`move-to-cluster-${getServerEnvironment()}`)
  );

  const [moveFilters, setMoveFilters] = useState<any>({
    exam: "SSC CGL",
    exam_id: 1,
    goal: "Tier 1 2025",
    goal_id: 5,
    section: "",
    section_id: "",
    topic: "",
    topic_id: "",
    subtopic: "",
    subtopic_id: "",
    cluster: "",
    cluster_id: "",
  });
  const [activeStep, setStep] = useState(0);
  const [localMasters, setLocalMasters] = useState(masters);
  const dPaths = localFiltersValues ? JSON.parse(atob(localFiltersValues)) : [];
  const recentSubtopics = dPaths;

  const commonSx = {
    width: "100%",
    "& .MuiInputBase-root": {
      padding: "5px 7px!important",
      borderRadius: "4px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "0 !important",
      maxWidth: "calc(100% - 50px) !important",
    },
  };

  const { exams, goals, sections, topics, subtopics, clusters } = localMasters;

  const getDifference = () => {
    if (cluster) {
      return filters.cluster_id === moveFilters.cluster_id;
    } else {
      return filters.subtopic_id === moveFilters.subtopic_id;
    }
  };

  // API calls for dependent dropdowns
  useEffect(() => {
    const fetchSections = async () => {
      if (moveFilters.goal_id) {
        const sections = await apiRequest({
          api: allApis.getSectionList(moveFilters.goal_id),
          method: "GET",
        });
        setLocalMasters((p) => ({ ...p, sections }));
      } else {
        setLocalMasters((p) => ({ ...p, sections: [] }));
      }
    };
    if (changeSubTopic) fetchSections();
  }, [moveFilters.goal_id]);

  useEffect(() => {
    const fetchTopics = async () => {
      if (moveFilters.section_id) {
        const topics = await apiRequest({
          api: allApis.getTopicsList(moveFilters.section_id),
          method: "GET",
        });
        setLocalMasters((p) => ({ ...p, topics }));
      } else {
        setLocalMasters((p) => ({ ...p, topics: [] }));
      }
    };
    if (changeSubTopic) fetchTopics();
  }, [moveFilters.section_id]);

  useEffect(() => {
    const fetchSubtopics = async () => {
      if (moveFilters.topic_id) {
        const subtopics = await apiRequest({
          api: allApis.getSubtopicslist(moveFilters.topic_id),
          method: "GET",
        });
        setLocalMasters((p) => ({ ...p, subtopics }));
      } else {
        setLocalMasters((p) => ({ ...p, subtopics: [] }));
      }
    };
    if (changeSubTopic) fetchSubtopics();
  }, [moveFilters.topic_id]);

  useEffect(() => {
    const fetchClusters = async () => {
      if (moveFilters.subtopic_id) {
        const clusters = await apiRequest({
          api: allApis.getClustersList(moveFilters.subtopic_id),
          method: "GET",
        });
        setLocalMasters((p) => ({ ...p, clusters }));
      } else {
        setLocalMasters((p) => ({ ...p, clusters: [] }));
      }
    };
    if (changeSubTopic) fetchClusters();
  }, [moveFilters.subtopic_id]);

  const getCluster: any = () => {
    return moveFilters.cluster.name
      ? moveFilters.cluster
      : clusters.find((s) => s.name === (moveFilters.cluster ?? "")) ?? {};
  };
  useEffect(() => {
    setTableData({
      actual: clusters,
      copy: clusters,
    });
  }, [clusters]);
  const steps: Step[] = [
    {
      label: "Subtopic",
      description: `Move to another Subtopic`,
      completed: true,
      content: () => (
        <Stack
          className="scrollBluePrimarythin"
          sx={{
            height: "100%",
            overflowY: "auto", // scroll only this
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: "10px",
            boxSizing: "border-box",
          }}
        >
          {/* Exam */}
          <Box>
            <Typography variant="caption">Exam</Typography>
            <CustomAutoComplete
              sx={commonSx}
              placeholder="All Exams"
              listLabel="name"
              disabled={exams.length === 1}
              list={exams}
              value={moveFilters.exam ?? ""}
              onChange={(val: any) =>
                setMoveFilters((prev: any) => ({
                  ...prev,
                  exam: val?.name || "",
                  exam_id: val?.id || "",
                }))
              }
              label=""
              error={false}
              helperText=""
            />
          </Box>

          {/* Goal */}
          <Box>
            <Typography variant="caption">Goal</Typography>
            <CustomAutoComplete
              sx={commonSx}
              listLabel="name"
              placeholder="All Goals"
              list={goals}
              value={goals.find((s) => s.name === (moveFilters.goal ?? ""))}
              onChange={(val: any) =>
                setMoveFilters((prev: any) => ({
                  ...prev,
                  goal: val?.name || "",
                  goal_id: val?.id || "",
                  section: "",
                  section_id: "",
                  topic: "",
                  topic_id: "",
                  subtopic: "",
                  subtopic_id: "",
                  cluster: "",
                  cluster_id: "",
                }))
              }
              label=""
              error={false}
              helperText=""
            />
          </Box>

          {/* Section */}
          <Box>
            <Typography variant="caption">Section</Typography>
            <CustomAutoComplete
              sx={commonSx}
              placeholder="All Sections"
              listLabel="name"
              list={sections}
              value={sections.find(
                (s) => s.name === (moveFilters.section ?? "")
              )}
              onChange={(val: any) =>
                setMoveFilters((prev: any) => ({
                  ...prev,
                  section: val ? val.name : "",
                  section_id: val ? val.id : "",
                  topic: "",
                  topic_id: "",
                  subtopic: "",
                  subtopic_id: "",
                  cluster: "",
                  cluster_id: "",
                }))
              }
              error={false}
              helperText=""
              label=""
            />
          </Box>

          {/* Topic */}
          <Box>
            <Typography variant="caption">Topic</Typography>
            <CustomAutoComplete
              sx={commonSx}
              listLabel="name"
              placeholder="All Topics"
              list={topics}
              value={topics.find((s) => s.name === (moveFilters.topic ?? ""))}
              onChange={(val: any) =>
                setMoveFilters((prev: any) => ({
                  ...prev,
                  topic: val?.name || "",
                  topic_id: val?.id || "",
                  subtopic: "",
                  subtopic_id: "",
                  cluster: "",
                  cluster_id: "",
                }))
              }
              label=""
              error={false}
              helperText=""
            />
          </Box>

          {/* Subtopic */}
          <Box>
            <Typography variant="caption">Subtopic</Typography>
            <CustomAutoComplete
              sx={commonSx}
              placeholder="All Subtopic"
              listLabel="name"
              list={subtopics}
              value={subtopics.find(
                (s) => s.name === (moveFilters.subtopic ?? "")
              )}
              onChange={(val: any) =>
                setMoveFilters((prev: any) => ({
                  ...prev,
                  subtopic: val?.name || "",
                  subtopic_id: val?.id || "",
                  cluster: "",
                  cluster_id: "",
                }))
              }
              label=""
              error={false}
              helperText=""
            />
          </Box>
        </Stack>
      ),
    },
    {
      label: "Cluster",
      description: `Move to another Cluster`,
      completed: true,
      content: () => (
        <Box width="100%" height="100%">
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" fontWeight="600" noWrap={false}>
              {moveFilters.cluster?.name ?`${moveFilters.cluster?.name}`: "Cluster"}{" "}
              {moveFilters.cluster?.name && (
                <Chip
                  label={`id: #${moveFilters.cluster?.id}`}
                  size="small"
                  sx={{
                    border: "1px solid rgb(0 0 0 / 60%)",
                    borderRadius: "4px",
                    padding: "0",
                    color: "rgb(0 0 0 / 60%)",
                    fontSize: "0.75rem",
                    background: "transparent",
                    "& .MuiChip-label": {
                      padding: "0 3px !important",
                    },
                    ...singleLineEllipsis,
                  }}
                />
              )}
              {(moveFilters.cluster.name
                ? moveFilters.cluster
                : clusters.find(
                    (s) => s.name === (moveFilters.cluster ?? "")
                  )) && (
                <StyledTooltip
                  arrow
                  placement="top"
                  title={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 1,
                        bgcolor: "background.paper",
                        maxWidth: 480,
                        minWidth: 320,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 1, color: "text.primary" }}
                      >
                        {getCluster().name} #{getCluster().id} Details
                      </Typography>

                      <Grid container spacing={1.5}>
                        <Grid item xs={4}>
                          <Typography variant="caption" fontWeight={500}>
                            Type
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().type}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" fontWeight={500}>
                            LOD Index
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().lod_index}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" fontWeight={500}>
                            Primary Language
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().primary_language}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="caption" fontWeight={500}>
                            Description
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().description}
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="caption" fontWeight={500}>
                            Time
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().min_time} – {getCluster().max_time}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" fontWeight={500}>
                            By SME
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().by_sme ? "Yes" : "No"}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" fontWeight={500}>
                            Exposure Index
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().exposure_index || "-"}
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="caption" fontWeight={500}>
                            Accuracy Index
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().accuracy_index || "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" fontWeight={500}>
                            Retention Index
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getCluster().retention_index || "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  }
                >
                  <IconButton size="small">
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </StyledTooltip>
              )}
            </Typography>
            <Box sx={{ maxWidth: "400px" }}>
              {" "}
              <CustomSearch
                placeholder="Search by name"
                copyData={tableData.copy}
                fieldName={["name"]}
                filteredData={(array: ObjectValues[]) => {
                  setTableData((prev) => {
                    return { ...prev, actual: array };
                  });
                }}
                onClear={() => {
                  setTableData((prev) => {
                    return { ...prev, actual: tableData.copy };
                  });
                }}
              />
            </Box>
          </Box>{" "}
          {tableData.actual.length > 0 ? (
            <Box
              className="scrollBluePrimarythin"
              sx={{
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: "10px",
                boxSizing: "border-box",
                height: "calc(100% - 33px)",
              }}
            >
              {tableData.actual.map(
                (option: {
                  id:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<
                        any,
                        string | React.JSXElementConstructor<any>
                      >
                    | Iterable<React.ReactNode>
                    | React.ReactPortal
                    | null
                    | undefined;
                  name:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<
                        any,
                        string | React.JSXElementConstructor<any>
                      >
                    | Iterable<React.ReactNode>
                    | React.ReactPortal
                    | null
                    | undefined;
                  description:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<
                        any,
                        string | React.JSXElementConstructor<any>
                      >
                    | Iterable<React.ReactNode>
                    | React.ReactPortal
                    | null
                    | undefined;
                  type: any;
                  lod_index: any;
                  primary_language: any;
                  min_time:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<
                        any,
                        string | React.JSXElementConstructor<any>
                      >
                    | Iterable<React.ReactNode>
                    | React.ReactPortal
                    | null
                    | undefined;
                  max_time:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<
                        any,
                        string | React.JSXElementConstructor<any>
                      >
                    | Iterable<React.ReactNode>
                    | React.ReactPortal
                    | null
                    | undefined;
                }) => {
                  const selected = option.id === moveFilters.cluster_id;
                  return (
                    <Card
                      sx={{
                        borderRadius: 2,
                        minHeight: "fit-content",
                        cursor: "grab",

                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: 4,
                          transform: "scale(1.01)",
                          border: "2px dashed #519C66",
                        },
                        boxShadow: selected ? 4 : "none",
                        transform: selected ? "scale(1.01)" : "none",
                        border: selected
                          ? "2px dashed #519C66"
                          : "1px dashed #ccc",
                        display: "flex",
                        flexDirection: "column",
                        p: 1.5,
                        width: "100%",
                        boxSizing: "border-box",
                        gap: 1, // spacing between sections
                        whiteSpace: "normal", // ✅ allow wrapping
                        wordBreak: "break-word", // ✅ wrap long words
                      }}
                      onClick={() => {
                        setMoveFilters((prev: any) => ({
                          ...prev,
                          cluster: option,
                          cluster_id: option?.id || "",
                        }));
                      }}
                    >
                      {/* Title */}
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        noWrap={false}
                      >
                        {option.name}{" "}
                        <Chip
                          label={`id: #${option.id}`}
                          size="small"
                          sx={{
                            border: "1px solid rgb(0 0 0 / 60%)",
                            borderRadius: "4px",
                            padding: "0",
                            color: "rgb(0 0 0 / 60%)",
                            fontSize: "0.75rem",
                            background: "transparent",
                            "& .MuiChip-label": {
                              padding: "0 3px !important",
                            },
                            ...singleLineEllipsis,
                          }}
                        />
                      </Typography>

                      {/* Description */}
                      {option.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 3, // ✅ limit lines if too long
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {option.description}
                        </Typography>
                      )}

                      {/* Meta Info */}
                      <Stack spacing={0.75}>
                        <Stack direction="row" spacing={1}>
                          <Typography variant="caption" fontWeight="500">
                            Type:
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.type || "-"}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                          <Typography variant="caption" fontWeight="500">
                            LOD Index:
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.lod_index || "-"}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                          <Typography variant="caption" fontWeight="500">
                            Language:
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.primary_language || "-"}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                          <Typography variant="caption" fontWeight="500">
                            Time:
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.min_time} – {option.max_time}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Card>
                  );
                }
              )}
            </Box>
          ) : (
            <Box  sx={{
                display: "flex",
                p: "10px",
                boxSizing: "border-box",
                height: "calc(100% - 33px)",justifyContent:'center',alignItems:'center'
              }}><Typography variant="caption">No clusters found.</Typography></Box>
          )}
        </Box>
      ),
    },
  ];

  return (
    <ConfirmationDialog
      sx={{
        "& .MuiDialog-container": {
          backdropFilter: "blur(3px)",
          margin: "10px 400px 10px 10px !important",
          width: "calc(100% - 410px)",
          height: "calc(100% - 20px)",
          "& .MuiDialog-paperScrollPaper": {
            margin: "0 10px 0 0",
            width: "calc(100% - 10px)",
            maxWidth: "900px",
            maxHeight: "800px",
            height: "100%",
          },
        },
      }}
      actions={[]}
      title={
        cluster
          ? `Choose the correct cluster of ${filters.subtopic}`
          : "Move Question to another subtopic"
      }
      content={
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
          }}
        >
          {/* LEFT COLUMN - Recently Used */}
          <Box
            sx={{
              width: "45%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              height: "100%", // full height
            }}
          >
            {/* Header - stays fixed */}
            <Box sx={{ px: "10px", boxSizing: "border-box" }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 500, mb: "2px" }}
              >
                Recently Used
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                * You can select any one path directly to move this question to
                from your previous paths.
              </Typography>
            </Box>

            {/* Scrollable list */}
            <Box
              className="scrollBluePrimarythin"
              sx={{
                flexGrow: 1, // takes remaining space
                overflowY: "auto", // scroll only this
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: "10px",
                boxSizing: "border-box",
              }}
            >
              {recentSubtopics.map((s: any, idx: number) => {
                const label = `${s.exam} > ${s.goal} > ${s.section} > ${
                  s.topic
                } > ${s.subtopic} > ${s.cluster.name ?? s.cluster}`;
                const selected = moveFilters.cluster_id === s.cluster_id;
                return (
                  <Tooltip key={idx} title={label}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        minHeight: "fit-content",
                        cursor: "grab",

                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: 4,
                          transform: "scale(1.01)",
                          border: "2px dashed #519C66",
                        },
                        boxShadow: selected ? 4 : "none",
                        transform: selected ? "scale(1.01)" : "none",
                        border: selected
                          ? "2px dashed #519C66"
                          : "1px dashed #ccc",
                      }}
                      onClick={() => setMoveFilters(s)}
                    >
                      <CardActionArea sx={{ p: 1.5, cursor: "grab" }}>
                        <Stack spacing={0.5}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {s.exam} • {s.goal}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {s.section}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {s.topic}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {s.subtopic}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {s.cluster.name ?? s.cluster}
                          </Typography>
                        </Stack>
                      </CardActionArea>
                    </Card>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
          <Box
            sx={{ borderLeft: "1px solid black", m: "0 7px", height: "100%" }}
          ></Box>
          {/* RIGHT COLUMN - Stepper */}
          <Box
            sx={{
              width: "55%",
              p: "10px",
              boxSizing: "border-box",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box maxWidth={"300px"} width={"100%"}>
              <CssStepperHorizontal
                activeStep={activeStep}
                alternativeLabel
                connector={<ColorlibConnector />}
                sx={{
                  "& .MuiStepConnector-root": {
                    left: "92px !important",
                    right: "92px !important",
                    width: "calc(100% - 184px) !important",
                  },
                }}
              >
                {steps.map(({ label }, i) => (
                  <Step key={label} expanded={true}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </CssStepperHorizontal>
            </Box>
            <Box
              sx={{
                m: "10px 0",
                height: "calc(100% - 78px)",
                width: "100%",
                p: "0 5px",
                boxSizing: "border-box",
              }}
            >
              {steps[activeStep].content()}
            </Box>
            <Box width="100%" display={"flex"} justifyContent={"space-between"}>
              {activeStep === 0 ? (
                <Box></Box>
              ) : (
                <CustomIconButton
                  {...{
                    disabled: false,
                    onClick: () => {
                      setStep(0);
                    },
                    sx: { minWidth: "fit-content" },
                    variant: "outlined",
                  }}
                  content="Back"
                  icon={<ArrowBack sx={{ height: "16px" }} />}
                />
              )}
              {activeStep === 0 ? (
                <CustomContainedButton
                  otherProps={{
                    disabled:
                      !moveFilters.subtopic_id ||
                      filters.subtopic_id === moveFilters.subtopic_id,
                    onClick: () => {
                      setStep(1);
                    },
                    sx: { minWidth: "60px" },
                  }}
                  icon="Next"
                  content={<ArrowForwardIcon sx={{ height: "16px" }} />}
                />
              ) : (
                <CustomIconButton
                  key={"Move"}
                  content={<NoteOutlinedIcon sx={{ height: "16px" }} />}
                  variant="outlined"
                  disabled={getDifference()}
                  icon={"Move"}
                  sx={{ minWidth: "fit-content", mr: "5px" }}
                  onClick={async () => {
                    if (!moveFilters.exam_id) {
                      return dispatch(
                        showSnack({
                          open: true,
                          message: "Select any exam to move.",
                          severity: "error",
                        })
                      );
                    }
                    if (!moveFilters.goal_id) {
                      return dispatch(
                        showSnack({
                          open: true,
                          message: "Select any goal to move.",
                          severity: "error",
                        })
                      );
                    }
                    if (!moveFilters.section_id) {
                      return dispatch(
                        showSnack({
                          open: true,
                          message: "Select any Section to move.",
                          severity: "error",
                        })
                      );
                    }
                    if (!moveFilters.topic_id) {
                      return dispatch(
                        showSnack({
                          open: true,
                          message: "Select any topic to move.",
                          severity: "error",
                        })
                      );
                    }
                    if (!moveFilters.subtopic_id) {
                      return dispatch(
                        showSnack({
                          open: true,
                          message: "Select any subtopic to move.",
                          severity: "error",
                        })
                      );
                    }
                    if (!moveFilters.cluster_id) {
                      return dispatch(
                        showSnack({
                          open: true,
                          message: "Select any cluster to move.",
                          severity: "error",
                        })
                      );
                    }

                    // Save to recent
                    // Check if moveFilters already exists in dPaths (deep compare)
                    const exists = dPaths?.some(
                      (item: any) =>
                        JSON.stringify(item) === JSON.stringify(moveFilters)
                    );
                    if (!exists) {
                      localStorage.setItem(
                        btoa(`move-to-cluster-${getServerEnvironment()}`),
                        btoa(
                          JSON.stringify(
                            dPaths
                              ? dPaths.length > 5
                                ? [moveFilters, ...dPaths.slice(0, 4)]
                                : [moveFilters, ...dPaths]
                              : [moveFilters]
                          )
                        )
                      );
                    }
                    onSubmit(moveFilters);
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      }
      customWidth="fit-content"
      open={changeSubTopic}
      onClose={() => {
        setChangeSubTopic(false);
      }}
      onSubmit={() => {}}
    />
  );
}

export default UpdateSubtopicTopicDialog;
